import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import type { CurrentPlan } from "@circle-react/types";
import { useBillingDetailsApi } from "../../hooks/useBillingDetailsApi";
import type { AuxiliarySubscriptionResponse } from "@circle-react/types/UsageBilling";
import { useAuxiliarySubscriptionApi } from "../../hooks/useAuxiliarySubscriptionApi";

interface BillingContextValue {
  auxiliarySubscription?: AuxiliarySubscriptionResponse;
  currentPlan?: CurrentPlan;
  errorFetchingAuxiliarySubscription: unknown;
  errorFetchingCurrentPlan: unknown;
  isAuxiliarySubscriptionLoading: boolean;
  isCurrentPlanLoading: boolean;
  refetchAuxiliarySubscription: () => unknown;
  refetchCurrentPlan: () => unknown;
  showAddons?: boolean;
  showBillingDetails?: boolean;
  showBillingPortal?: boolean;
}

export const BillingContext = createContext<BillingContextValue>({
  auxiliarySubscription: undefined,
  currentPlan: undefined,
  errorFetchingAuxiliarySubscription: null,
  errorFetchingCurrentPlan: null,
  isAuxiliarySubscriptionLoading: false,
  isCurrentPlanLoading: false,
  refetchAuxiliarySubscription: () => {},
  refetchCurrentPlan: () => {},
  showAddons: false,
  showBillingDetails: false,
  showBillingPortal: false,
});

BillingContext.displayName = "BillingContext";

interface BillingProviderProps {
  children: ReactNode;
  showAddons?: boolean;
  showBillingDetails?: boolean;
  showBillingPortal?: boolean;
}

export const BillingProvider = ({
  showAddons,
  showBillingDetails,
  showBillingPortal,
  children,
}: BillingProviderProps) => {
  const {
    currentPlan,
    isCurrentPlanLoading,
    errorFetchingCurrentPlan,
    refetchCurrentPlan,
  } = useBillingDetailsApi();

  const {
    auxiliarySubscription,
    isAuxiliarySubscriptionLoading,
    errorFetchingAuxiliarySubscription,
    refetchAuxiliarySubscription,
  } = useAuxiliarySubscriptionApi();

  return (
    <BillingContext.Provider
      value={{
        auxiliarySubscription,
        currentPlan,
        errorFetchingAuxiliarySubscription,
        errorFetchingCurrentPlan,
        isAuxiliarySubscriptionLoading,
        isCurrentPlanLoading,
        refetchAuxiliarySubscription,
        refetchCurrentPlan,
        showAddons,
        showBillingDetails,
        showBillingPortal,
      }}
    >
      {children}
    </BillingContext.Provider>
  );
};

export const useBillingContext = () => useContext(BillingContext);
